/**
 * Message translations for chat error messages, status messages, and UI text
 * Supports: en, fr, de, es, it, pt
 */

import type { SupportedLanguage } from './languageUtils';

type MessageKey =
    | 'timeout_error'
    | 'network_error'
    | 'validation_error'
    | 'generic_error'
    | 'rate_limited'
    | 'still_processing'
    | 'retry_button';

type MessageTranslations = Record<SupportedLanguage, Record<MessageKey, string>>;

const MESSAGE_TRANSLATIONS: MessageTranslations = {
    en: {
        timeout_error:
            '⏱️ The request timed out. The server is taking too long to respond. Please try again.',
        network_error:
            '⚠️ Connection error occurred. Please check your internet connection and try again.',
        validation_error:
            'Sorry, there was a technical issue processing your request. Our team has been notified and will investigate.',
        generic_error: 'Sorry, there was an error processing your request',
        rate_limited:
            "I'm still finishing your previous message. Please wait a few seconds, then try again.",
        still_processing: '⏳ Still processing... this is taking longer than expected',
        retry_button: 'Try again',
    },
    fr: {
        timeout_error:
            '⏱️ La requête a expiré. Le serveur met trop de temps à répondre. Veuillez réessayer.',
        network_error:
            '⚠️ Erreur de connexion. Veuillez vérifier votre connexion internet et réessayer.',
        validation_error:
            "Désolé, un problème technique s'est produit lors du traitement de votre demande. Notre équipe a été notifiée et va enquêter.",
        generic_error: "Désolé, une erreur s'est produite lors du traitement de votre demande",
        rate_limited:
            'Je termine encore votre message précédent. Patientez quelques secondes, puis réessayez.',
        still_processing: '⏳ Traitement en cours... cela prend plus de temps que prévu',
        retry_button: 'Réessayer',
    },
    de: {
        timeout_error:
            '⏱️ Die Anfrage ist abgelaufen. Der Server braucht zu lange zum Antworten. Bitte versuchen Sie es erneut.',
        network_error:
            '⚠️ Verbindungsfehler aufgetreten. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.',
        validation_error:
            'Entschuldigung, es gab ein technisches Problem bei der Verarbeitung Ihrer Anfrage. Unser Team wurde benachrichtigt und wird es untersuchen.',
        generic_error:
            'Entschuldigung, bei der Verarbeitung Ihrer Anfrage ist ein Fehler aufgetreten',
        rate_limited:
            'Ich bearbeite noch Ihre vorherige Nachricht. Bitte warten Sie einige Sekunden und versuchen Sie es erneut.',
        still_processing: '⏳ Verarbeitung läuft... dies dauert länger als erwartet',
        retry_button: 'Erneut versuchen',
    },
    es: {
        timeout_error:
            '⏱️ La solicitud ha caducado. El servidor está tardando demasiado en responder. Por favor, inténtelo de nuevo.',
        network_error:
            '⚠️ Error de conexión. Por favor, compruebe su conexión a internet e inténtelo de nuevo.',
        validation_error:
            'Lo sentimos, hubo un problema técnico al procesar su solicitud. Nuestro equipo ha sido notificado y lo investigará.',
        generic_error: 'Lo sentimos, hubo un error al procesar su solicitud',
        rate_limited:
            'Todavía estoy terminando su mensaje anterior. Espere unos segundos e inténtelo de nuevo.',
        still_processing: '⏳ Procesando... esto está tardando más de lo esperado',
        retry_button: 'Intentar de nuevo',
    },
    it: {
        timeout_error:
            '⏱️ La richiesta è scaduta. Il server sta impiegando troppo tempo a rispondere. Per favore, riprova.',
        network_error:
            '⚠️ Errore di connessione. Per favore, controlla la tua connessione internet e riprova.',
        validation_error:
            "Siamo spiacenti, si è verificato un problema tecnico nell'elaborazione della tua richiesta. Il nostro team è stato notificato e indagherà.",
        generic_error:
            "Siamo spiacenti, si è verificato un errore nell'elaborazione della tua richiesta",
        rate_limited:
            'Sto ancora completando il tuo messaggio precedente. Attendi qualche secondo e riprova.',
        still_processing: '⏳ Elaborazione in corso... sta richiedendo più tempo del previsto',
        retry_button: 'Riprova',
    },
    pt: {
        timeout_error:
            '⏱️ O pedido expirou. O servidor está a demorar demasiado tempo a responder. Por favor, tente novamente.',
        network_error:
            '⚠️ Ocorreu um erro de ligação. Por favor, verifique a sua ligação à internet e tente novamente.',
        validation_error:
            'Lamentamos, ocorreu um problema técnico ao processar o seu pedido. A nossa equipa foi notificada e irá investigar.',
        generic_error: 'Lamentamos, ocorreu um erro ao processar o seu pedido',
        rate_limited:
            'Ainda estou a terminar a sua mensagem anterior. Aguarde alguns segundos e tente novamente.',
        still_processing: '⏳ A processar... está a demorar mais do que o esperado',
        retry_button: 'Tentar novamente',
    },
    bg: {
        timeout_error: '⏱️ Заявката изтече. Сървърът отговаря твърде бавно. Моля, опитайте отново.',
        network_error:
            '⚠️ Възникна грешка при свързването. Проверете интернет връзката си и опитайте отново.',
        validation_error:
            'Съжаляваме, възникна технически проблем при обработването на заявката ви. Екипът ни е уведомен и ще го провери.',
        generic_error: 'Съжаляваме, възникна грешка при обработването на заявката ви',
        rate_limited:
            'Все още довършвам предишното ви съобщение. Изчакайте няколко секунди и опитайте отново.',
        still_processing: '⏳ Все още обработвам... това отнема повече време от очакваното',
        retry_button: 'Опитайте отново',
    },
    nl: {
        timeout_error:
            '⏱️ De aanvraag is verlopen. De server doet er te lang over om te reageren. Probeer het opnieuw.',
        network_error:
            '⚠️ Er is een verbindingsfout opgetreden. Controleer je internetverbinding en probeer het opnieuw.',
        validation_error:
            'Sorry, er is een technisch probleem opgetreden bij het verwerken van je aanvraag. Ons team is op de hoogte gebracht en onderzoekt het.',
        generic_error: 'Sorry, er is een fout opgetreden bij het verwerken van je aanvraag',
        rate_limited:
            'Ik rond je vorige bericht nog af. Wacht een paar seconden en probeer het opnieuw.',
        still_processing: '⏳ Nog steeds bezig... dit duurt langer dan verwacht',
        retry_button: 'Opnieuw proberen',
    },
    ro: {
        timeout_error:
            '⏱️ Cererea a expirat. Serverul răspunde prea greu. Vă rugăm să încercați din nou.',
        network_error:
            '⚠️ A apărut o eroare de conexiune. Verificați conexiunea la internet și încercați din nou.',
        validation_error:
            'Ne pare rău, a apărut o problemă tehnică la procesarea cererii dvs. Echipa noastră a fost notificată și va investiga.',
        generic_error: 'Ne pare rău, a apărut o eroare la procesarea cererii dvs.',
        rate_limited:
            'Încă finalizez mesajul dvs. anterior. Așteptați câteva secunde, apoi încercați din nou.',
        still_processing: '⏳ Încă se procesează... durează mai mult decât era de așteptat',
        retry_button: 'Încercați din nou',
    },
    hu: {
        timeout_error:
            '⏱️ A kérés időtúllépés miatt megszakadt. A szerver túl lassan válaszol. Próbálja újra.',
        network_error:
            '⚠️ Kapcsolati hiba történt. Ellenőrizze internetkapcsolatát, majd próbálja újra.',
        validation_error:
            'Sajnáljuk, technikai probléma történt a kérés feldolgozása közben. Csapatunk értesítést kapott, és kivizsgálja azt.',
        generic_error: 'Sajnáljuk, hiba történt a kérés feldolgozása közben',
        rate_limited:
            'Még az előző üzenetét fejezem be. Várjon néhány másodpercet, majd próbálja újra.',
        still_processing: '⏳ Feldolgozás folyamatban... ez a vártnál tovább tart',
        retry_button: 'Próbálja újra',
    },
};

/**
 * Get translated message for a given key and language
 * @param key - The message key to translate
 * @param language - The target language
 * @param fallbackMessage - Optional fallback message if translation not found
 * @returns Translated message
 */
export const getMessage = (
    key: MessageKey,
    language: SupportedLanguage = 'en',
    fallbackMessage?: string
): string => {
    const translation = MESSAGE_TRANSLATIONS[language]?.[key];
    if (translation) {
        return translation;
    }

    // Fallback to English if language not found
    const englishTranslation = MESSAGE_TRANSLATIONS.en[key];
    if (englishTranslation) {
        return englishTranslation;
    }

    // Ultimate fallback
    return fallbackMessage || `Translation missing for: ${key}`;
};

/**
 * Get error message with optional error details appended
 * @param key - The error message key
 * @param language - The target language
 * @param errorDetails - Optional error details to append
 * @returns Formatted error message
 */
export const getErrorMessage = (
    key: MessageKey,
    language: SupportedLanguage = 'en',
    errorDetails?: string
): string => {
    const baseMessage = getMessage(key, language);

    if (errorDetails && key === 'generic_error') {
        const preview =
            errorDetails.length > 200 ? `${errorDetails.substring(0, 200)}...` : errorDetails;
        return `${baseMessage}: ${preview}`;
    }

    return baseMessage;
};
